// import {window} from 'global';
import {Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DomSanitizer, Meta, SafeHtml, Title} from '@angular/platform-browser';
import {isPlatformBrowser} from '@angular/common';
import {CategoriesService, Comment, CommentsService, Deal, DealsService, User, UserLevels, UserService} from '../core';
import {ServerResponse} from '../core/models/server-response.model';
import MiscUtil from '../core/utils/misc.util';
import Utils from '../utils';
import {ConfirmationService, MenuItem, MessageService} from 'primeng/api';
import {concatMap, tap} from 'rxjs/operators';
import {of} from 'rxjs';
import {AuthComponent} from '../auth/auth.component';
import {DialogService, DynamicDialogRef} from 'primeng/dynamicdialog';
import {ErrorUtil} from '../core/utils/error.util';
import {TranslateService} from '@ngx-translate/core';
import AssetsUtil from '../core/utils/assets.util';
import {Category} from '../core/models/category.model';
import {NavigationService} from '../core/services/navigation.service';
import {environment} from '../../environments/environment';

declare var gtag: Function;
@Component({
  selector: 'app-deal-page',
  templateUrl: './deal.component.html',
  providers: [MessageService, ConfirmationService]
})
export class DealComponent implements OnInit {
  private readonly isBrowser: Boolean;
  constructor(
      private route: ActivatedRoute,
      private dealsService: DealsService,
      private commentsService: CommentsService,
      private router: Router,
      private userService: UserService,
      private sanitizer: DomSanitizer,
      private dealService: DealsService,
      private dialogService: DialogService,
      private messageService: MessageService,
      private confirmService: ConfirmationService,
      private translate: TranslateService,
      private categoriesService: CategoriesService,
      private navigationService: NavigationService,
      @Inject(PLATFORM_ID) platformId: Object,
      private titleService: Title,
      private metaService: Meta) {
    this.isBrowser = isPlatformBrowser(platformId);
  }
  deal: Deal;
  currentUser: User;
  canModify: boolean;
  comments: Comment[] = [];
  chunkComments:  Array<Comment[]>;
  numberOfComments = 0;
  commentsPerPage = 5;
  newCommentContent = '';
  isSubmitting = false;
  isDeleting = false;
  needCommentPaginator = false;
  isAuthenticated = false;
  isExpired = false;
  dealCategory: Category;
  ref: DynamicDialogRef;

  dealSafeContent: SafeHtml;
  carouselRespOpt = [{
    breakpoint: '1300px',
    numVisible: 5,
    numScroll: 4
  },
    {
      breakpoint: '1100px',
      numVisible: 4,
      numScroll: 3
    },
    {
      breakpoint: '950px',
      numVisible: 3,
      numScroll: 3
    },
    {
      breakpoint: '700px',
      numVisible: 2,
      numScroll: 2
    },
    {
      breakpoint: '250px',
      numVisible: 1,
      numScroll: 1
    }];
  items: MenuItem[];
  loading = true;
  dealImageFallback = AssetsUtil.loadDealFallbackOnError();
  ngOnInit() {
    // Retrieve the prefetched deal
    this.route.data.subscribe(
        (data) => {
          this.deal = data.deal.deal;
          this.deal.relatedDeals = data.deal.relatedDeals;
          this.dealSafeContent = this.sanitizer.bypassSecurityTrustHtml(this.deal.link_content);
          this.isExpired = (this.deal.link_expired > 0);
          this.populateComments();
          this.loading = false;
          this.categoriesService.currentCategories.subscribe(
              (next: Category[]) => {
                next.forEach( cat => {
                  if (cat.category_id === this.deal.link_category) {
                    this.dealCategory = cat;
                  }
                });
              }
          );
          this.onActivate(null);
          this.titleService.setTitle(this.deal.link_title);
          this.metaService.addTags([
            {name: 'og:title', content: this.deal.link_title},
            {name: 'og:type', content: 'deal'},
            {name: 'og:url', content: environment.server_url + this.router.url},
            {name: 'og:image', content: this.deal.file_name},
            {name: 'twitter:title', content: this.deal.link_title},
            {name: 'twitter:url', content: environment.server_url + this.router.url},
            {name: 'twitter:image', content: this.deal.file_name}
          ]);
        }
    );
    this.userService.currentUser.subscribe(
        (userData: User) => {
          this.currentUser = userData;
          this.canModify = (this.currentUser.userID === this.deal.link_author) ||
              userData.level === UserLevels.Moderator ||
              userData.level === UserLevels.Admin;
          this.setupOptionMenu();
        }
    );
    this.userService.isAuthenticated.subscribe( auth =>
        this.isAuthenticated = auth
    );
  }
  onToggleFavorite(favorited: boolean) {
    // this.deal.link_shipping = favorited;

    if (favorited) {
      this.deal.link_votes++;
    } else {
      this.deal.link_votes--;
    }
  }

  onToggleFollowing(following: boolean) {
    // this.deal.link_author.following = following;
  }

  populateComments() {
    this.commentsService.getAll(this.deal.link_id)
        .subscribe(comments => this.handleComments(comments));
  }
  handleComments(comments) {
    this.numberOfComments = Utils.countProps(comments);
    this.chunkComments = MiscUtil.chunkArrayInGroups(comments, this.commentsPerPage);
    if (Utils.countProps(this.chunkComments) > 0) {
      this.comments = this.chunkComments[0];
    }
    this.needCommentPaginator = this.numberOfComments > this.commentsPerPage;
  }
  addComment() {
    this.isSubmitting = true;
    const commentBody = this.newCommentContent;
    this.commentsService
        .add(this.deal.link_id, commentBody)
        .subscribe(
            comment => {
              this.numberOfComments = Utils.countProps(this.comments);
              if (this.numberOfComments > 0) {
                this.comments.unshift(comment);
              } else {
                this.comments.push(comment);
              }
              this.numberOfComments += 1;
              this.newCommentContent = '';
              this.isSubmitting = false;
            },
            errors => {
              this.isSubmitting = false;
            }
        );
  }

  onDeleteComment(comment: Comment) {
    this.confirmService.confirm({
      message: this.translate.instant('comment_delete_prompt'),
      accept: () => {
        this.commentsService.destroy(comment.comment_id, this.deal.link_id)
            .subscribe(
                success => {
                  this.comments = this.comments.filter((item) => item !== comment);
                }
            );
      },
      acceptLabel: this.translate.instant('yes'),
      rejectLabel: this.translate.instant('no')
    });
  }

  onEditComment(comment: Comment) {
    this.commentsService.edit(comment)
        .subscribe( data =>
            this.handleUpadteReponse(data, comment)
        );
  }

  goToUrl() {
      gtag('event', 'click', {
        'event_category': 'go_to_deal',
        'event_label': this.deal.link_url
      });
      gtag('event', 'conversion', {
        'send_to': environment.gtag_conversion,
        'link_id': this.deal.link_id
      });
    window.open(this.deal.link_url);
  }

  votePress(value: number) {
    if (this.deal.vote_value === 10 || this.deal.vote_value === -10) {
      // already voted
      this.messageService.add({severity: 'warn', summary: this.translate.instant('deal_vote_already')});
      return;
    }
    if (value > 0 ) {
      // UpVote
      this.userService.isAuthenticated.pipe(concatMap(
          (authenticated) => {
            // Not authenticated? Push to login screen
            if (!authenticated) {
              this.needLogin();
              return of(null);
            }
            return this.dealService.upVote(this.deal.link_id).pipe(tap(
                data => this.handleServerResponse(data, value)
            ));
          }
      )).subscribe();
    } else {
      // DownVote
      this.userService.isAuthenticated.pipe(concatMap(
          (authenticated) => {
            // Not authenticated? Push to login screen
            if (!authenticated) {
              this.needLogin();
              return of(null);
            }
            return this.dealService.downVote(this.deal.link_id).pipe(tap(
                data => this.handleServerResponse(data, value)
            ));
          }
      )).subscribe();
    }

  }
  voteRemovePress(value: number) {
    this.dealService.voteRemove(this.deal.link_id).pipe(tap(
        data => this.handleServerResponse(data, value, true)
    )).subscribe();
  }
  handleServerResponse(resp: ServerResponse, value, isRemove = false) {
    if (resp['ok']) {
      if (isRemove) {
        this.deal.link_votes -= value / 10;
        this.deal.vote_value = 0;
      } else {
        this.deal.link_votes += value / 10;
        this.deal.vote_value = value;
      }

    }
  }
  private handleUpadteReponse(data: ServerResponse, newComment: Comment) {
    if (data.ok) {
      const index = this.comments.findIndex(comment => comment.comment_id === newComment.comment_id);
      newComment.isEditing = false;
      this.comments[index] = newComment;
    } else {
      //Show popup
    }
  }
  needLogin(register = false) {
    this.ref = this.dialogService.open(AuthComponent, {
      styleClass: 'login-popup',
      dismissableMask: true,
      data: (register) ? 'signup' : 'login'
    });

    this.ref.onClose.subscribe((product) =>
        this.ref.destroy()
    );
  }
  commentPageChanged($event: any) {
    this.comments = this.chunkComments[$event.page];
  }
  setupOptionMenu() {
    this.items = [
      {
        icon: 'pi pi-share',
        label: this.translate.instant('Social Content'),
        command: () => {
          this.copySocialContent()
        },
        visible: this.currentUser.level === UserLevels.Admin,
        disabled: this.currentUser.level !== UserLevels.Admin
      },
      {
        icon: 'pi pi-facebook',
        label: this.translate.instant('share'),
        url: 'https://www.facebook.com/sharer/sharer.php?u=' + environment.server_url + this.router.url
      },
      {
        icon: 'pi pi-twitter',
        label: this.translate.instant('share'),
        url: 'http://twitter.com/intent/tweet?url=' + environment.server_url + this.router.url + '&text=' + this.deal.link_title + '&via=' + this.translate.instant('twitter_account')
      },
      {
        icon: 'pi pi-clock',
        label: this.translate.instant('expired'),
        command: () => {
          this.confirmService.confirm({
            message: this.translate.instant('deal_expired_prompt'),
            accept: () => {
              this.dealService.setAvailability(this.deal.link_id, true).pipe(tap( resp => {
                    if (resp.ok) {
                      this.messageService.add({ severity: 'info', detail: this.translate.instant('deal_expired_ok') });
                      this.deal.link_expired = 1;
                      this.isExpired = true;
                      this.setupOptionMenu();
                    }
                    return resp;
                  }, error => {
                    this.messageService.add({ severity: 'error',
                      summary: this.translate.instant('error'),
                      detail: this.translate.instant(new ErrorUtil(error['error']).description) });

                    return error;
                  }
              )).subscribe();
            },
            acceptLabel: this.translate.instant('yes'),
            rejectLabel: this.translate.instant('no')
          });
        },
        visible: !this.isExpired,
        disabled: this.isExpired
      },
      {
        icon: 'pi pi-clock',
        label: this.translate.instant('available'),
        command: () => {
          this.confirmService.confirm({
            message: this.translate.instant('deal_available_prompt'),
            accept: () => {
              this.dealService.setAvailability(this.deal.link_id, false).pipe(tap( resp => {
                    if (resp.ok) {
                      this.messageService.add({ severity: 'info', detail: this.translate.instant('deal_available_ok') });
                      this.deal.link_expired = 0;
                      this.isExpired = false;
                      this.setupOptionMenu();
                    }
                    return resp;
                  }, error => {
                    this.messageService.add({ severity: 'error', summary: this.translate.instant('error'), detail: this.translate.instant(new ErrorUtil(error['error']).description) });

                    return error;
                  }
              )).subscribe();
            },
            acceptLabel: this.translate.instant('yes'),
            rejectLabel: this.translate.instant('no')
          });
        },
        visible: this.isExpired,
        disabled: !this.isExpired
      },
      {
        icon: 'pi pi-pencil',
        label: this.translate.instant('edit'),
        command: () => {
          this.router.navigate(['/edit', this.deal.link_id]);
        },
        visible: this.canModify,
        disabled: !this.canModify,
      },
      {
        icon: 'pi pi-trash',
        label: this.translate.instant('delete'),
        command: () => {
          this.confirmService.confirm({
            message: this.translate.instant('deal_delete_prompt'),
            accept: () => {
              this.dealService.destroy(this.deal.link_id).pipe(tap( resp => {
                    if (resp.ok) {
                      this.messageService.add({ severity: 'success', detail: this.translate.instant('deal_delete_ok') });
                      setTimeout(() => {
                        this.navigationService.goBack();
                      }, 2000);
                    }
                    return resp;
                  }, error => {
                    this.messageService.add({ severity: 'error',
                      summary: this.translate.instant('error'),
                      detail: this.translate.instant(new ErrorUtil(error['error']).description) });
                    return error;
                  }
              )).subscribe();
            },
            acceptLabel: this.translate.instant('yes'),
            rejectLabel: this.translate.instant('no')
          });
        },
        visible: this.canModify,
        disabled: !this.canModify,
      }
    ];
  }
  onActivate(event) {
    if (this.isBrowser) {
      window.scroll({
        top: 0,
        left: 0
      });
    }
  }
  copySocialContent() {
    MiscUtil.copyToClipboard(this.deal.socialContent);
    this.messageService.add({ severity: 'success',
      summary: 'Social content copied in clipboard'});
  }
  copyPromoCode() {
    MiscUtil.copyToClipboard(this.deal.link_promo_code);
    this.messageService.add({ severity: 'success',
      summary: this.translate.instant('promo_code_copied')});
  }
}
